import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '@fluentui/react-components';
import { LockShieldRegular } from '@fluentui/react-icons';
import { saveMail } from '../../../services/api/dataServiceOutlookQuickSave';
import { ISaveInfo, IAccessProperties } from '../model/QuickSaveModel';
import { IDocumentDetails } from '../../../@types/models';
import { getDocumentDetailComp } from '../../../common/components/home/HomeComponents';
import type {
    InsertableArchiveResult,
    EmailDocumentCategoryResult,
    CaseResult,
    RecordTypeResult,
    CarbonCopyContactResult
} from '../../../services/api/dataServiceOutlookQuickSave';
import { PageHeader } from '../components/PageHeader';
import { AccessRightsForm } from './components/AccessRightsForm';
import { PublicTitleSection } from './components/PublicTitleSection';
import { useAccessRightsData } from './hooks/useAccessRightsData';
import { useAccessReviewStyles } from './styles';
import { navigateToImportArchive } from '../utils/importArchiveNavigation';
import { navigateSafely } from '../../../utils/navigation';

interface PreservedState {
    selectedArchive?: InsertableArchiveResult;
    selectedCategory?: EmailDocumentCategoryResult;
    selectedCase?: CaseResult;
    selectedRecordType?: RecordTypeResult;
    selectedCopyTo?: CarbonCopyContactResult[];
    title?: string;
    isRestricted?: boolean;
}



export interface AccessReviewLocationState {
    saveInfo: ISaveInfo;
    preservedState?: PreservedState;
}

const AccessReviewPage: React.FC = () => {
    const { t } = useTranslation();
    const styles = useAccessReviewStyles();
    const navigate = useNavigate();
    const location = useLocation();
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitError, setSubmitError] = useState('');
    const [documentDetails, setDocumentDetails] = useState<IDocumentDetails | null>(null);
    const [publicTitleOption, setPublicTitleOption] = useState<string>('equal');
    const [manualPublicTitle, setManualPublicTitle] = useState<string>('');

    const state = location.state as AccessReviewLocationState;

    const documentArchive = state?.saveInfo?.documentArchive ? Number.parseInt(state.saveInfo.documentArchive) : undefined;

    const {
        accessCodes,
        lawCodes,
        accessGroups,
        selectedAccessCode,
        setSelectedAccessCode,
        selectedParagraph,
        setSelectedParagraph,
        selectedAccessGroup,
        setSelectedAccessGroup,
        isLoadingAccessCodes,
        isLoadingLawCodes,
        isLoadingAccessGroups,
        error: dataError,
    } = useAccessRightsData({
        documentArchive,
        initialAccessProperties: state?.saveInfo?.accessProps
    });

    const handleBack = () => {
        // Navigate back with preserved state
        navigateSafely(navigate, '/quicksave', { state: undefined });
    };

    const handleSave = () => {
        if (!state?.saveInfo) {
            setSubmitError(t('Missing save information'));
            return;
        }

        // Validate required fields
        if (!selectedAccessCode) {
            setSubmitError(t('Please select an access code'));
            return;
        }

        // Check if paragraph is required based on selected access code
        const selectedAccessCodeObj = accessCodes.find(ac => ac.Recno.toString() === selectedAccessCode);
        if (selectedAccessCodeObj?.IsParagraphRequired && !selectedParagraph) {
            setSubmitError(t('Please select a paragraph'));
            return;
        }

        if (!selectedAccessGroup) {
            setSubmitError(t('Please select an access group'));
            return;
        }

        setIsSubmitting(true);
        setSubmitError('');

        const restrictedAccessProperties: IAccessProperties = {
            AccessCode: Number.parseInt(selectedAccessCode),
            AccessGroup: Number.parseInt(selectedAccessGroup),
            Paragraph: selectedParagraph,
            ArchiveHasAccessCode: 1,
            ArchiveHasAccessGroup: 1,
            IsRestricted: true,
        };

        // Determine public title based on radio selection
        let publicTitle: string | undefined;
        if (publicTitleOption === 'manual' && manualPublicTitle.trim()) {
            publicTitle = manualPublicTitle.trim();
        } else if (publicTitleOption === 'equal') {
            publicTitle = state.saveInfo.documentTitle;
        }

        const updatedSaveInfo: ISaveInfo = {
            ...state.saveInfo,
            accessProps: restrictedAccessProperties,
            publicTitle: publicTitle || undefined,
        };

        void saveMail(
            updatedSaveInfo,
            (response: IDocumentDetails | null) => {
                setIsSubmitting(false);

                // Check if this is an import archive response
                if (response?.IsInImportArchive) {
                    // Navigate to import archive page with error details
                    navigateToImportArchive(navigate, response);
                } else {
                    setDocumentDetails(response);
                }
            },
            (error: string) => {
                setIsSubmitting(false);
                setSubmitError(error);
            }
        );
    };

    // If document saved successfully, show document details
    if (documentDetails) {
        return (
            <div className={styles.accessReviewContainer}>
                {getDocumentDetailComp(documentDetails)}
            </div>
        );
    }

    return (
        <div className={styles.accessReviewContainer}>
            <PageHeader
                title={t('Access Rights')}
                description={t('Modify who can see this document')}
                icon={<LockShieldRegular fontSize={20} />}
            />

            <div className={styles.accessReviewContentContainer}>
                <AccessRightsForm
                    accessCodes={accessCodes}
                    selectedAccessCode={selectedAccessCode}
                    onAccessCodeChange={setSelectedAccessCode}
                    isLoadingAccessCodes={isLoadingAccessCodes}
                    lawCodes={lawCodes}
                    selectedParagraph={selectedParagraph}
                    onParagraphChange={setSelectedParagraph}
                    isLoadingLawCodes={isLoadingLawCodes}
                    accessGroups={accessGroups}
                    selectedAccessGroup={selectedAccessGroup}
                    onAccessGroupChange={setSelectedAccessGroup}
                    isLoadingAccessGroups={isLoadingAccessGroups}
                />

                <PublicTitleSection
                    publicTitleOption={publicTitleOption}
                    onPublicTitleOptionChange={setPublicTitleOption}
                    manualPublicTitle={manualPublicTitle}
                    onManualPublicTitleChange={setManualPublicTitle}
                />

                {(submitError || dataError) && (
                    <div className={styles.accessReviewErrorText}>
                        {submitError || dataError}
                    </div>
                )}

                <div className={styles.accessReviewButtonContainer}>
                    <Button
                        appearance="secondary"
                        onClick={handleBack}
                    >
                        {t('Cancel')}
                    </Button>
                    <Button
                        appearance="primary"
                        onClick={handleSave}
                        disabled={isSubmitting || !selectedAccessCode || !selectedAccessGroup}
                    >
                        {isSubmitting ? t('Saving...') : t('Save Document')}
                    </Button>
                </div>
            </div>
        </div>
    );
};

export default AccessReviewPage;
